import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpContextToken, HttpClient, HttpResponse, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { API_CONFIG } from '../config/api.config';

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private http = inject(HttpClient);

    private formatErrors(error: any) {
        return throwError(() => error);
    }

    private buildUrl(path: string): string {
        if (/^https?:\/\//i.test(path)) return path;
        const normalized = path.startsWith('/') ? path : `/${path}`;
        return `${API_CONFIG.API_BASE_URL}${normalized}`;
    }

    get<T>(path: string, params: HttpParams = new HttpParams()): Observable<T> {
        return this.http.get<T>(this.buildUrl(path), { params }).pipe(catchError(this.formatErrors));
    }

    getBlob(path: string, params: HttpParams = new HttpParams()): Observable<HttpResponse<Blob>> {
        return this.http.get(this.buildUrl(path), { params, responseType: 'blob', observe: 'response' });
    }

    post<T>(path: string, body: Object = {}, paramsOrOptions?: HttpParams | { params?: HttpParams; context?: HttpContext }): Observable<T> {
        let params = new HttpParams();
        let context: HttpContext | undefined;
        if (paramsOrOptions instanceof HttpParams) {
            params = paramsOrOptions;
        } else if (paramsOrOptions) {
            params = paramsOrOptions.params ?? new HttpParams();
            context = paramsOrOptions.context;
        }
        const options: any = { params };
        if (context) options.context = context;
        return (this.http.post<T>(this.buildUrl(path), body, options) as Observable<T>).pipe(catchError(this.formatErrors));
    }

    put<T>(path: string, body: Object = {}, params: HttpParams = new HttpParams()): Observable<T> {
        return this.http.put<T>(this.buildUrl(path), body, { params }).pipe(catchError(this.formatErrors));
    }

    patch<T>(path: string, body: Object = {}, params: HttpParams = new HttpParams()): Observable<T> {
        return this.http.patch<T>(this.buildUrl(path), body, { params }).pipe(catchError(this.formatErrors));
    }

    delete<T>(path: string, options: HttpParams | { params?: HttpParams; body?: unknown } = new HttpParams()): Observable<T> {
        let params = new HttpParams();
        let body: unknown = undefined;
        if (options instanceof HttpParams) {
            params = options;
        } else if (options) {
            params = options.params ?? new HttpParams();
            body = options.body;
        }
        const reqOptions: any = { params };
        if (body !== undefined) reqOptions.body = body;
        return (this.http.delete<T>(this.buildUrl(path), reqOptions) as Observable<T>).pipe(catchError(this.formatErrors));
    }
}
